// File: packages/agent-kit/src/conversations/useConversation/index.ts
import { useEffect } from 'react';
import { agentTestChatService as chatSvc, conversationService } from '@dopomogai/api-client';
import type { SSEMessageEventData, InitTestChatResponse, UUID } from '@dopomogai/types';
import { useChatSession } from './useChatSession';
import { useConversationState } from './useConversationState';
import { buildConversationActions } from './conversationActions';

export interface UseConversationOptions {
  onSseEvent?: (event: SSEMessageEventData) => void;
  onSessionInit?: (config: InitTestChatResponse) => void;
  authMode?: 'test' | 'widget';
  widgetInit?: { widgetId?: string; templateId?: string; apiBaseUrlOverride?: string };
}

export interface UseConversationDependencies {
  chatService: typeof chatSvc;
  conversationService: typeof conversationService;
}

export const useConversation = (
  agentId: UUID | null,
  activeConversationId: UUID | null,
  options?: UseConversationOptions,
  dependencies?: Partial<UseConversationDependencies>
) => {
  const { sessionRef, internalAgentId, setInternalAgentId, ensureSession, applyOpenHeaders } = useChatSession(agentId, {
    authMode: options?.authMode,
    widgetInit: options?.widgetInit,
    onSessionInit: options?.onSessionInit,
  });

  const chatService = dependencies?.chatService || chatSvc;
  const convSvc = dependencies?.conversationService || conversationService;

  const {
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    isSynthesizing,
    setIsSynthesizing,
    error,
    setError,
    usage,
    setUsage,
    streamConversationId,
    setStreamConversationId,
    streamControllerRef,
    assistantMessageIdRef,
    streamingContentRef,
    clear,
  } = useConversationState(sessionRef);

  useEffect(() => {
    if (options?.authMode !== 'widget') {
      setInternalAgentId(agentId);
    }
  }, [agentId, options?.authMode, setInternalAgentId]);

  // Eagerly initialize widget session so visitor history can load on mount
  useEffect(() => {
    if (options?.authMode === 'widget') {
      void ensureSession().catch(() => {});
    }
  }, [options?.authMode, ensureSession]);

  const { loadHistory, sendMessage, submitToolResult, retryMessage } = buildConversationActions({
    agentId,
    activeConversationId,
    internalAgentId,
    chatService,
    conversationService: convSvc,
    ensureSession,
    applyOpenHeaders,
    sessionRef,
    options,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    isSynthesizing,
    setIsSynthesizing,
    setError,
    setUsage,
    streamConversationId,
    setStreamConversationId,
    assistantMessageIdRef,
    streamingContentRef,
    streamControllerRef,
  });

  // FIX: Handle conversation switching logic without wiping active streams
  useEffect(() => {
    const auth = options?.authMode || 'test';
  
    if (auth === 'widget') {
      // Adoption Case Guard: 
      // If we are switching to the ID we are currently streaming, do NOT wipe messages.
      if (activeConversationId && streamConversationId === activeConversationId) {
        return;
      }
      
      // Special case for New Chat placeholder: 
      // If activeConversationId is null, we expect empty messages unless we are transiently holding them.
      // Controller manages transient state, so we can clear here safely for explicit switches.
      
      // Only clear if we are explicitly changing to a different, non-matching ID
      setStreamConversationId(null);
      setMessages([]);
      setError(null);
      assistantMessageIdRef.current = null;
      streamingContentRef.current = '';
      return;
    }
  
    // TEST MODE reset logic
    setStreamConversationId(null);
    setMessages([]);
    setError(null);
    assistantMessageIdRef.current = null;
    streamingContentRef.current = '';
  
    if (activeConversationId) {
      loadHistory(activeConversationId);
    }
  }, [activeConversationId, options?.authMode, streamConversationId]);

  return {
    messages,
    setMessages,
    sendMessage,
    submitToolResult,
    retryMessage,
    isLoading: isLoading || isSynthesizing,
    error,
    conversationId: streamConversationId || activeConversationId,
    clear,
    loadHistory,
    usage,
  };
};